import { useEffect, useMemo, useState } from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  makeStyles,
  Chip,
} from '@material-ui/core';
import {
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = {
  pending: '#ff9800',
  approved: '#4caf50',
  denied: '#f44336',
  primary: '#2196f3',
};

const CHART_COLORS = ['#2196f3', '#9c27b0', '#ff9800', '#4caf50', '#f44336', '#00bcd4', '#ff5722'];

const useStyles = makeStyles(theme => ({
  metricCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  metricTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  metricIcon: {
    fontSize: '2.5rem',
    opacity: 0.3,
  },
  projectRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));

interface RequestMetrics {
  total: number;
  pending: number;
  approved: number;
  denied: number;
  byType: { [key: string]: number };
  byProject: { [key: string]: number };
  recent: Array<{
    name: string;
    type: string;
    status: string;
    date: string;
  }>;
  timeline: Array<{
    date: string;
    count: number;
  }>;
}

export const PlatformInsightsCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [metrics, setMetrics] = useState<RequestMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    const checkPermissionAndFetchMetrics = async () => {
      try {
        console.log('🔍 Platform Insights: Starting permission check...');

        // Check if user is platform_admin
        const identity = await identityApi.getBackstageIdentity();
        const userName = identity.userEntityRef.split('/')[1];
        const userRef = identity.userEntityRef;

        console.log('👤 Platform Insights: Checking for user:', userName);

        // Get user email
        let userEmail = userName;
        try {
          const userEntity = await catalogApi.getEntityByRef(userRef);
          userEmail = (userEntity?.spec?.profile as any)?.email || userName;
        } catch (err) {
          console.warn('⚠️ Platform Insights: Could not fetch user entity:', err);
        }

        // Check for impersonated user (for development/testing)
        const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
        const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
        const impersonatedEmail = impersonatedFromStorage || impersonatedFromConfig;

        // Use impersonated email if available
        let effectiveUserEmail = userEmail;
        if (impersonatedEmail && impersonatedEmail !== userName) {
          console.log('🎭 Platform Insights: Using impersonated user:', impersonatedEmail);
          effectiveUserEmail = impersonatedEmail;
        }

        console.log('📧 Platform Insights: Effective user email:', effectiveUserEmail);

        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;

        console.log('🔐 Platform Insights: Checking platform_admin permission via OpenFGA...');

        const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${effectiveUserEmail}`,
            action: 'platform_admin',
            resource: 'platform:main',
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const isAdmin = data.allowed || false;
          setIsPlatformAdmin(isAdmin);

          console.log(`${isAdmin ? '✅' : '❌'} Platform Insights: User is ${isAdmin ? '' : 'NOT '}platform_admin`);
          console.log('Response data:', data);

          if (isAdmin) {
            // Fetch metrics only if user is platform_admin
            console.log('📊 Platform Insights: Fetching metrics...');
            await fetchMetrics();
          }
        } else {
          const errorText = await response.text();
          console.warn('⚠️ Platform Insights: Permission check failed:', response.status, errorText);
        }
      } catch (error) {
        console.error('❌ Platform Insights: Error checking permissions or fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    checkPermissionAndFetchMetrics();
  }, [catalogApi, config, identityApi]);

  const [projectsMap, setProjectsMap] = useState<Map<string, string>>(new Map());

  const fetchMetrics = async () => {
    try {
      // Get all Request entities
      const { items } = await catalogApi.getEntities({
        filter: {
          kind: 'Request',
        },
      });

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const metrics: RequestMetrics = {
        total: items.length,
        pending: 0,
        approved: 0,
        denied: 0,
        byType: {},
        byProject: {},
        recent: [],
        timeline: [],
      };

      // Timeline data - initialize last 30 days
      const timelineMap: { [key: string]: number } = {};
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateKey = date.toISOString().split('T')[0];
        timelineMap[dateKey] = 0;
      }

      items.forEach(entity => {
        // Count by status
        const tags = entity.metadata.tags || [];
        const approvalTag = tags.find(tag => tag.startsWith('approval-status:'));
        const status = approvalTag ? approvalTag.split(':')[1] : 'unknown';

        if (status === 'pending') metrics.pending++;
        else if (status === 'approved') metrics.approved++;
        else if (status === 'denied') metrics.denied++;

        // Count by type
        const type = String(entity.spec?.type || 'unknown');
        metrics.byType[type] = (metrics.byType[type] || 0) + 1;

        // Count by project
        const projectRef = String(entity.spec?.project || 'unknown');

        if (projectRef !== 'unknown') {
          metrics.byProject[projectRef] =
            (metrics.byProject[projectRef] || 0) + 1;
        }

        // Recent requests and timeline (last 30 days)
        const requestDate = entity.spec?.requestDate ? new Date(String(entity.spec.requestDate)) : null;
        if (requestDate && requestDate > thirtyDaysAgo) {
          const dateKey = requestDate.toISOString().split('T')[0];
          if (timelineMap[dateKey] !== undefined) {
            timelineMap[dateKey]++;
          }

          metrics.recent.push({
            name: entity.metadata.name,
            type,
            status,
            date: requestDate.toISOString(),
          });
        }
      });

      // Convert timeline map to array
      metrics.timeline = Object.entries(timelineMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, count]) => ({
          date: new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
          count,
        }));

      // Sort recent by date (newest first)
      metrics.recent.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      metrics.recent = metrics.recent.slice(0, 10); // Keep only top 10

      setMetrics(metrics);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const topProjects = useMemo(() => {
    return Object.entries(metrics?.byProject || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  }, [metrics]);

  useEffect(() => {
    const resolveProjects = async () => {
      if (!topProjects.length) return;

      try {
        const projectRefs = topProjects.map(([ref]) => ref);

        const { items } = await catalogApi.getEntities({
          filter: projectRefs.map(ref => {
            const [kind, rest] = ref.split(':');
            const [namespace, name] = rest.split('/');

            return {
              kind: kind || 'Group',
              'metadata.name': name,
              'metadata.namespace': namespace || 'default',
            };
          }),
        });

        const map = new Map<string, string>();

        items.forEach(entity => {
          const ref = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;

          const displayName =
            entity.metadata.title || entity.metadata.name;

          map.set(ref, displayName);
        });

        setProjectsMap(map);

      } catch (err) {
        console.error('❌ Error resolving projects:', err);
      }
    };

    resolveProjects();
  }, [topProjects]);

  if (loading) {
    return (
      <InfoCard title="Platform Insights">
        <Box p={2}>
          <Progress />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Checking permissions...
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  if (!isPlatformAdmin) {
    console.log('🔒 Platform Insights: User is not platform_admin, showing access denied');
    return (
      <InfoCard title="Platform Insights">
        <Box p={2}>
          <Typography variant="h6" gutterBottom color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            This page is only accessible to platform administrators.
            If you believe you should have access, please contact your system administrator.
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  if (!metrics) {
    console.log('⚠️ Platform Insights: No metrics data available');
    return (
      <InfoCard title="Platform Insights">
        <Box p={2}>
          <Typography variant="body1" color="textSecondary">
            No metrics available. This could be due to:
          </Typography>
          <ul>
            <li>No Request entities in the catalog</li>
            <li>Error fetching data from the catalog API</li>
          </ul>
        </Box>
      </InfoCard>
    );
  }

  console.log('✅ Platform Insights: Rendering metrics', metrics);

  const topTypes = Object.entries(metrics.byType)
    .sort(([, a], [, b]) => b - a);

  // Prepare data for charts
  const statusData = [
    { name: 'Pending', value: metrics.pending, color: COLORS.pending },
    { name: 'Approved', value: metrics.approved, color: COLORS.approved },
    { name: 'Denied', value: metrics.denied, color: COLORS.denied },
  ].filter(item => item.value > 0);

  const typeData = topTypes.map(([name, value]) => ({ name, value }));

  return (
    <InfoCard title="Platform Insights" subheader="Request Metrics & Usage Statistics">
      <Grid container spacing={3}>
        {/* Metric Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className={classes.metricTitle}>Total Requests</Typography>
                  <Typography className={classes.metricValue}>{metrics.total}</Typography>
                </Box>
                <AssignmentIcon className={classes.metricIcon} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className={classes.metricTitle}>Pending</Typography>
                  <Typography className={classes.metricValue} style={{ color: '#ff9800' }}>
                    {metrics.pending}
                  </Typography>
                </Box>
                <ScheduleIcon className={classes.metricIcon} style={{ color: '#ff9800' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className={classes.metricTitle}>Approved</Typography>
                  <Typography className={classes.metricValue} style={{ color: '#4caf50' }}>
                    {metrics.approved}
                  </Typography>
                </Box>
                <CheckCircleIcon className={classes.metricIcon} style={{ color: '#4caf50' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography className={classes.metricTitle}>Denied</Typography>
                  <Typography className={classes.metricValue} style={{ color: '#f44336' }}>
                    {metrics.denied}
                  </Typography>
                </Box>
                <CancelIcon className={classes.metricIcon} style={{ color: '#f44336' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Request Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Requests by Type
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill={COLORS.primary}>
                    {typeData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Timeline Chart - Full Width */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Request Timeline (Last 30 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics.timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke={COLORS.primary}
                    strokeWidth={2}
                    name="Requests"
                    dot={{ fill: COLORS.primary, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Request Types */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Request Types (Details)
              </Typography>
              <Box mt={2}>
                {topTypes.map(([type, count]) => (
                  <Box key={type} className={classes.projectRow}>
                    <Typography variant="body1">{type}</Typography>
                    <Chip label={count} color="primary" size="small" />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Projects */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Top Projects (by requests)
              </Typography>
              <Box mt={2}>
                {topProjects.length > 0 ? (
                  topProjects.map(([project, count]) => (
                    <Box key={project} className={classes.projectRow}>
                      <Typography variant="body1">
                        {projectsMap.get(project) ? `${project.split('/').pop()?.toUpperCase()} - ${projectsMap.get(project)}` : project.split('/').pop()}
                      </Typography>
                      <Chip label={count} color="secondary" size="small" />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No project data available
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity (last 30 days) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity (Last 30 Days)
              </Typography>
              <Box mt={2}>
                {metrics.recent.length > 0 ? (
                  metrics.recent.map((req, idx) => (
                    <Box key={idx} className={classes.projectRow}>
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {req.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {req.type} • {new Date(req.date).toLocaleDateString('es-ES')}
                        </Typography>
                      </Box>
                      <Chip
                        label={req.status}
                        size="small"
                        style={{
                          backgroundColor:
                            req.status === 'pending'
                              ? '#ff9800'
                              : req.status === 'approved'
                                ? '#4caf50'
                                : '#f44336',
                          color: 'white',
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No recent activity in the last 30 days
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
